import { X } from 'lucide-react';
import { Dialog as D } from 'radix-ui';
import { useEffect, useState, type ReactNode } from 'react';
import { cn } from '../cn';
import { Button } from './button';

export interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  /** Action row, right-aligned. Primary action last. */
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Prevent closing by overlay click / Escape (e.g. while a payment is processing). */
  dismissible?: boolean;
  className?: string;
}

const widths = { sm: 'max-w-sm', md: 'max-w-lg', lg: 'max-w-2xl', xl: 'max-w-4xl' };

/** Modal dialog. Use only when the task needs protected focus (payment, confirmation). */
export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  size = 'md',
  dismissible = true,
  className,
}: DialogProps) {
  return (
    <D.Root open={open} onOpenChange={(v) => (dismissible || v ? onOpenChange(v) : undefined)}>
      <D.Portal>
        <D.Overlay className="fixed inset-0 z-50 bg-ink/30 animate-fade-in" />
        <D.Content
          onOpenAutoFocus={(e) => {
            // Start where the work is: the first field, else the first footer action
            // (Cancel comes first, so Enter never confirms something destructive by accident).
            const root = e.currentTarget as HTMLElement;
            const target =
              root.querySelector<HTMLElement>('[data-autofocus], input:not([disabled]):not([type=hidden]), textarea:not([disabled])') ??
              root.querySelector<HTMLElement>('[data-dialog-footer] button:not([disabled])');
            if (target) {
              e.preventDefault();
              target.focus();
            }
          }}
          onEscapeKeyDown={(e) => !dismissible && e.preventDefault()}
          onPointerDownOutside={(e) => !dismissible && e.preventDefault()}
          className={cn(
            'fixed top-1/2 left-1/2 z-50 flex max-h-[calc(100dvh-2rem)] w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2 flex-col',
            'rounded-xl bg-surface shadow-dialog animate-pop-in focus:outline-none',
            widths[size],
            className,
          )}
        >
          <div className="flex items-start gap-4 px-6 pt-5 pb-4">
            <div className="min-w-0 flex-1">
              <D.Title className="text-lg font-semibold text-ink">{title}</D.Title>
              {description ? (
                <D.Description className="mt-1 text-base text-ink-3">{description}</D.Description>
              ) : (
                <D.Description className="sr-only">
                  {typeof title === 'string' ? title : ''}
                </D.Description>
              )}
            </div>
            {dismissible && (
              <D.Close
                className="-mr-2 rounded-md p-2 text-ink-3 transition-colors hover:bg-rail hover:text-ink"
                aria-label="Закрыть"
              >
                <X className="size-5" />
              </D.Close>
            )}
          </div>
          {children && <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-5">{children}</div>}
          {footer && (
            <div data-dialog-footer className="flex items-center justify-end gap-2 border-t border-line px-6 py-4">
              {footer}
            </div>
          )}
        </D.Content>
      </D.Portal>
    </D.Root>
  );
}

/** Side panel for editing a record without leaving the list. */
export function Sheet({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  width = 'md',
}: Omit<DialogProps, 'size' | 'dismissible' | 'className'> & { width?: 'md' | 'lg' }) {
  return (
    <D.Root open={open} onOpenChange={onOpenChange}>
      <D.Portal>
        <D.Overlay className="fixed inset-0 z-50 bg-ink/20 animate-fade-in" />
        <D.Content
          className={cn(
            'fixed inset-y-0 right-0 z-50 flex w-full flex-col bg-surface shadow-dialog animate-sheet-in focus:outline-none',
            width === 'md' ? 'max-w-xl' : 'max-w-3xl',
          )}
        >
          <div className="flex items-start gap-4 border-b border-line px-6 py-4">
            <div className="min-w-0 flex-1">
              <D.Title className="text-lg font-semibold">{title}</D.Title>
              {description ? (
                <D.Description className="mt-0.5 text-sm text-ink-3">{description}</D.Description>
              ) : (
                <D.Description className="sr-only">
                  {typeof title === 'string' ? title : ''}
                </D.Description>
              )}
            </div>
            <D.Close
              className="-mr-2 rounded-md p-2 text-ink-3 transition-colors hover:bg-rail hover:text-ink"
              aria-label="Закрыть"
            >
              <X className="size-5" />
            </D.Close>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">{children}</div>
          {footer && (
            <div className="flex items-center justify-end gap-2 border-t border-line px-6 py-4">
              {footer}
            </div>
          )}
        </D.Content>
      </D.Portal>
    </D.Root>
  );
}

/**
 * A screen inside a screen: the whole viewport bar a small inset, for a task
 * that is its own piece of work rather than an interruption — browsing the
 * catalogue while a check is open, for instance. Use a `Dialog` when the task
 * needs protected focus and a `Sheet` when it is editing one record.
 */
export function FullScreen({
  open,
  onOpenChange,
  title,
  description,
  header,
  children,
  closeLabel = 'Закрыть',
  onOpened,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  /** The bar across the top; the close button is added after it. */
  header?: ReactNode;
  children: ReactNode;
  closeLabel?: string;
  /** Runs instead of the default autofocus — point it at the search field. */
  onOpened?: () => void;
}) {
  return (
    <D.Root open={open} onOpenChange={onOpenChange}>
      <D.Portal>
        <D.Overlay className="fixed inset-0 z-50 bg-ink/40 animate-fade-in" />
        <D.Content
          className="fixed inset-3 z-50 flex flex-col overflow-hidden rounded-2xl floor shadow-dialog animate-pop-in focus:outline-none"
          onOpenAutoFocus={(e) => {
            if (!onOpened) return;
            e.preventDefault();
            onOpened();
          }}
        >
          <D.Title className="sr-only">{title}</D.Title>
          <D.Description className="sr-only">{description ?? title}</D.Description>
          <header className="flex h-16 shrink-0 items-center gap-4 px-4">
            {header}
            <D.Close
              aria-label={closeLabel}
              className="flex size-11 shrink-0 items-center justify-center rounded-xl text-ink-3 transition-colors duration-150 hover:bg-surface hover:text-ink"
            >
              <X className="size-5" />
            </D.Close>
          </header>
          {children}
        </D.Content>
      </D.Portal>
    </D.Root>
  );
}

// ───────────────────────── Confirm ─────────────────────────

interface ConfirmRequest {
  title: string;
  description?: string;
  confirmLabel?: string;
  tone?: 'danger' | 'primary';
  resolve: (ok: boolean) => void;
}

let pending: ConfirmRequest | null = null;
const confirmListeners = new Set<() => void>();

/** Asks for confirmation in the design-system dialog. Resolves true/false. */
export function confirmAction(opts: Omit<ConfirmRequest, 'resolve'>): Promise<boolean> {
  return new Promise((resolve) => {
    pending?.resolve(false);
    pending = { ...opts, resolve };
    confirmListeners.forEach((l) => l());
  });
}

/** Mount once per app, next to the Toaster. */
/** Keys closer together than this are a scanner, not a hand. */
const SCAN_GAP_MS = 60;

export function ConfirmHost() {
  const [, force] = useState(0);
  useEffect(() => {
    const l = () => force((n) => n + 1);
    confirmListeners.add(l);
    return () => void confirmListeners.delete(l);
  }, []);
  const req = pending;
  const close = (ok: boolean) => {
    req?.resolve(ok);
    pending = null;
    force((n) => n + 1);
  };

  // A barcode scanner types the code and ends with Enter, all inside a few
  // milliseconds. With a confirmation open and its button focused, that Enter
  // would answer «Удалить?» or «Очистить чек?» on the cashier's behalf. A key
  // that follows the previous one faster than a person can type is a
  // scanner's, and its Enter is swallowed.
  const open = req !== null;
  useEffect(() => {
    if (!open) return;
    let last = 0;
    const onKey = (e: KeyboardEvent) => {
      const now = performance.now();
      const burst = now - last < SCAN_GAP_MS;
      last = now;
      if (e.key === 'Enter' && burst) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener('keydown', onKey, { capture: true });
    return () => window.removeEventListener('keydown', onKey, { capture: true });
  }, [open]);
  return (
    <Dialog
      open={req !== null}
      onOpenChange={(v) => !v && close(false)}
      size="sm"
      title={req?.title ?? ''}
      description={req?.description}
      footer={
        <>
          <Button onClick={() => close(false)}>Отмена</Button>
          <Button autoFocus variant={req?.tone === 'primary' ? 'primary' : 'danger'} onClick={() => close(true)}>
            {req?.confirmLabel ?? 'Подтвердить'}
          </Button>
        </>
      }
    />
  );
}
